import React, { useState, useEffect } from 'react';
import { Megaphone, AlertCircle, Info, AlertTriangle, ChevronRight } from 'lucide-react';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase';

const DashboardAnnouncements = ({ onNavigate }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRecentAnnouncements();
  }, []);

  const loadRecentAnnouncements = async () => {
    try {
      const q = query(
        collection(db, 'announcements'),
        orderBy('createdAt', 'desc'),
        limit(3)
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAnnouncements(data);
    } catch (error) {
      console.error('Erreur lors du chargement des annonces:', error);
    } finally {
      setLoading(false);
    }
  };

  const getImportanceIcon = (importance) => {
    switch (importance) {
      case 'urgent':
        return <AlertTriangle size={18} className="text-red-500" />;
      case 'important':
        return <AlertCircle size={18} className="text-orange-500" />;
      default:
        return <Info size={18} className="text-blue-500" />;
    }
  };

  const getImportanceColor = (importance) => {
    switch (importance) {
      case 'urgent':
        return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
      case 'important':
        return 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800';
      default:
        return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (announcements.length === 0) {
    return null; // Ne rien afficher s'il n'y a pas d'annonces
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <Megaphone size={24} className="text-blue-600" />
          Annonces Récentes
        </h2>
        {onNavigate && (
          <button
            onClick={() => onNavigate('announcements')}
            className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-semibold flex items-center gap-1"
          >
            Voir tout
            <ChevronRight size={16} />
          </button>
        )}
      </div>

      {/* Liste des annonces */}
      <div className="space-y-3">
        {announcements.map((announcement) => (
          <div
            key={announcement.id}
            className={`border rounded-lg p-4 ${getImportanceColor(announcement.importance)} cursor-pointer hover:shadow-md transition-shadow`}
            onClick={() => onNavigate && onNavigate('announcements')}
          >
            <div className="flex items-start gap-3">
              {getImportanceIcon(announcement.importance)}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-800 dark:text-white text-sm mb-1 line-clamp-1">
                  {announcement.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 text-xs line-clamp-2">
                  {announcement.content}
                </p>
                <p className="text-gray-500 dark:text-gray-400 text-xs mt-2">
                  {announcement.createdAt?.toDate ?
                    announcement.createdAt.toDate().toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'short'
                    }) :
                    'Date inconnue'
                  }
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Message informatif */}
      <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <p className="text-xs text-blue-800 dark:text-blue-200">
          Activez les notifications pour recevoir les annonces importantes en temps réel&nbsp;!
        </p>
      </div>
    </div>
  );
};

export default DashboardAnnouncements;